import { chainNameBySymbol, currencyNameBySymbol } from '@frontend/utils';
import { PageFields_CurrenciesPage_Fragment as CurrenciesPageFragment } from '../../graphql/generated';
import {
  ApiChainSymbols,
  ApiCurrencySymbols,
  binancePayAsBlockchain,
  binancePayDefaultCurrency,
  blockchainOrder,
  currencyOrder,
} from '../../utils';
import { getIsEVM, getIsMvx } from '../Payment/utils/common';
import { Currency } from './CurrencySelection';
import { PaymentMethod } from '../../graphql/customTypes';

const networkFeeData: Record<string, { lowestFeeNetworks: ApiChainSymbols[] }> =
  {
    USDT: {
      lowestFeeNetworks: [ApiChainSymbols.POLYGON],
    },
    USDC: {
      lowestFeeNetworks: [
        ApiChainSymbols.POLYGON,
        ApiChainSymbols.SOL,
        ApiChainSymbols.SUI,
        ApiChainSymbols.HEDERA,
      ],
    },
  };

const getLowestFeeNetworks = (symbol: string) => {
  return networkFeeData[symbol]?.lowestFeeNetworks || [];
};

const createNetworkObject = (
  symbol: string,
  lowestFeeNetworks: string[],
  available: boolean,
  reason: string
) => {
  const network = chainNameBySymbol[symbol || ''];

  return {
    gasFee: '',
    network,
    symbol,
    available,
    reason,
    isLowestFeeNetwork: lowestFeeNetworks.includes(symbol),
  };
};

const sortBlockchains = (
  blockchains: Currency['blockchains'],
  lowestFeeNetworks: ApiChainSymbols[]
) => {
  return [...blockchains].sort((a, b) => {
    const indexA = lowestFeeNetworks.indexOf(a.chainSymbol as ApiChainSymbols);
    const indexB = lowestFeeNetworks.indexOf(b.chainSymbol as ApiChainSymbols);

    if (indexA !== -1 && indexB === -1) return -1;
    if (indexA === -1 && indexB !== -1) return 1;
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;

    return (
      blockchainOrder.indexOf(a.chainSymbol as ApiChainSymbols) -
      blockchainOrder.indexOf(b.chainSymbol as ApiChainSymbols)
    );
  });
};

export const getNetworks = (
  currencies: Currency[],
  selectedCurrencySymbol: string
) => {
  const selectedCurrency = currencies.find(
    (c) => c.symbol === selectedCurrencySymbol
  );
  if (!selectedCurrency) return [];

  const lowestFeeNetworks = getLowestFeeNetworks(selectedCurrencySymbol);
  const sortedBlockchains = sortBlockchains(
    selectedCurrency.blockchains || [],
    lowestFeeNetworks
  );

  return sortedBlockchains.map((blockchain) =>
    createNetworkObject(
      blockchain.chainSymbol,
      lowestFeeNetworks,
      blockchain.available,
      blockchain.reason
    )
  );
};

export const getIsUSDT = (symbol: string) => {
  return symbol === ApiCurrencySymbols.USDT;
};

export const isCurrencyAvailable = (blockchains: Currency['blockchains']) => {
  return blockchains.some((blockchain) => blockchain.available);
};

export const sortCurrencies = (currencies: Currency[]): Currency[] => {
  return [...currencies]
    .sort((a, b) => {
      const aAvailable = isCurrencyAvailable(a.blockchains);
      const bAvailable = isCurrencyAvailable(b.blockchains);

      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;

      return (
        currencyOrder.indexOf(a.symbol as ApiCurrencySymbols) -
        currencyOrder.indexOf(b.symbol as ApiCurrencySymbols)
      );
    })
    .map((currency) => {
      const lowestFeeNetworks = getLowestFeeNetworks(currency.symbol);

      return {
        ...currency,
        blockchains: sortBlockchains(currency.blockchains, lowestFeeNetworks),
      };
    });
};

export const mapCurrencies = (
  currencies: CurrenciesPageFragment['pageData']['cryptoCurrencies']
) => {
  return (
    currencies?.map((c) => ({
      name: currencyNameBySymbol[c?.currency || ''],
      symbol: c?.currency || '',
      blockchains:
        c?.blockchains?.map((b) => ({
          chainSymbol: b?.blockchain || '',
          available: b?.available || false,
          reason: b?.reason || '',
        })) || [],
    })) || []
  );
};

export const getListableCurrencies = (
  currencies: Currency[],
  selectedMethod?: PaymentMethod
) => {
  return currencies
    .filter((c) => c.symbol !== ApiCurrencySymbols.UTK)
    .map((c) => ({
      ...c,
      blockchains: c.blockchains.filter((b) => {
        if (b.chainSymbol === binancePayAsBlockchain) return false;

        if (selectedMethod !== PaymentMethod.EXCHANGE) return true;

        const exchangeExcludedChains = [
          ApiChainSymbols.SOL,
          ApiChainSymbols.HEDERA,
        ];
        return !exchangeExcludedChains.includes(
          b.chainSymbol as ApiChainSymbols
        );
      }),
    }));
};

export const getBinancePayBlockchainData = (currencies: Currency[]) => {
  return currencies
    ?.find((c) => c?.symbol === binancePayDefaultCurrency)
    ?.blockchains?.find((b) => b?.chainSymbol === binancePayAsBlockchain);
};

export const getPageTypeFromBlockchains = (currency: Currency | undefined) => {
  if (!currency) return undefined;

  const hasEVM = currency.blockchains.some((b) =>
    getIsEVM(b.chainSymbol as ApiChainSymbols)
  );
  const hasMvx = currency.blockchains.some((b) =>
    getIsMvx(b.chainSymbol as ApiChainSymbols)
  );

  if (hasEVM) return 'evm';
  if (hasMvx) return 'mvx';
  return undefined;
};
